import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ClientErrorContext, ClientErrorEvent, ClientErrorKind } from 'src/app/models/client-error-report.model';
import { environment } from 'src/environments/environment';
import version from '../../../assets/version.json';
import { TokenService } from '../auth/token.service';

export const CLIENT_ERRORS_TELEMETRY_PATH = '/api/v1/client-telemetry/errors';

/** Limits the backend DTO enforces; anything longer would get the whole batch rejected. */
const MAX_MESSAGE = 500;
const MAX_STACK = 2000;
const MAX_NAME = 100;
export const MAX_BATCH = 20;

export const FLUSH_DELAY_MS = 10000;
/** Distinct errors per page load. A render loop throwing every frame must not become a flood. */
export const MAX_DISTINCT_PER_SESSION = 100;
/** Events held while there is no session to send them with (login page, expired token). */
const MAX_PENDING = 50;

const SAFE_ROUTE = /^[A-Za-z0-9_\-/.]{1,300}$/;
const CALL_SID = /^CA[0-9a-f]{32}$/;

/** Browser noise that says nothing about our code. */
const IGNORED_MESSAGES = [/ResizeObserver loop/i];

const APP_VERSION = `${version.major}.${version.minor}.${version.patch}`;

/**
 * Sends errors seen in the browser to welby-micro-service, which writes them to Cloud Logging
 * (`jsonPayload.message="Client error reported"`), so a failure that only one care manager saw can
 * be found and alerted on.
 *
 * Events are folded by kind + message (a repeat only bumps `count`) and sent in batches every
 * {@link FLUSH_DELAY_MS}, plus once more when the page is hidden. Each distinct error is sent once
 * per page load: repeats after its batch has gone out are dropped. The server redacts phones, emails
 * and query strings; the browser only keeps the route free of a query.
 *
 * Sent through `HttpBackend`, bypassing the interceptors, for the same reason as
 * `TwilioService.reportClientCallError`: a report sent with a stale token must never sign the user
 * out. Nothing here may throw: telemetry must not break the code that is reporting.
 */
@Injectable({ providedIn: 'root' })
export class ClientErrorReporter implements OnDestroy {
  private readonly http: HttpClient;
  private readonly pending = new Map<string, ClientErrorEvent>();
  private readonly seen = new Set<string>();
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  /** Bound once, so the same reference can be removed in ngOnDestroy. */
  private readonly onPageHidden: () => void;

  constructor(
    httpBackend: HttpBackend,
    private router: Router,
    private tokenService: TokenService,
    private zone: NgZone
  ) {
    this.http = new HttpClient(httpBackend);
    this.onPageHidden = () => {
      if (document.visibilityState === 'hidden') {
        this.flush(true);
      }
    };
    document.addEventListener('visibilitychange', this.onPageHidden);
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.onPageHidden);
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
    }
  }

  report(kind: ClientErrorKind, error: unknown, context: ClientErrorContext = {}): void {
    try {
      this.enqueue(kind, error, context);
    } catch {
      // Telemetry must never break the code that is reporting.
    }
  }

  /** Sends what is pending. `keepalive` lets the request outlive the page when it is being closed. */
  flush(keepalive = false): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    // Called from a timer and from a visibilitychange listener: a throw here would be an uncaught
    // error in the app, raised by the very code meant to report errors.
    try {
      this.sendPending(keepalive);
    } catch {
      // The batch is only cleared once the request is built, so whatever is still pending is retried.
      if (this.pending.size) {
        this.scheduleFlush();
      }
    }
  }

  private sendPending(keepalive: boolean): void {
    const token = this.tokenService.getToken();
    if (!this.pending.size) {
      return;
    }
    if (!token || this.tokenService.tokenIsExpired()) {
      // Kept and retried: the session may come back (login, token refresh).
      this.scheduleFlush();
      return;
    }

    const events = [...this.pending.values()];
    this.pending.clear();
    const url = `${environment.welbyEndpoint}${CLIENT_ERRORS_TELEMETRY_PATH}`;
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

    for (let i = 0; i < events.length; i += MAX_BATCH) {
      const body = { appVersion: APP_VERSION, events: events.slice(i, i + MAX_BATCH) };
      if (keepalive && typeof fetch === 'function') {
        // HttpClient cannot outlive the page; fetch with keepalive can (sendBeacon cannot carry the token).
        fetch(url, { method: 'POST', headers, body: JSON.stringify(body), keepalive: true }).catch(() => undefined);
      } else {
        this.http.post(url, body, { headers }).subscribe({ error: () => undefined });
      }
    }
  }

  private enqueue(kind: ClientErrorKind, error: unknown, context: ClientErrorContext): void {
    const ownMessage = this.messageOf(error);
    if (IGNORED_MESSAGES.some((pattern) => pattern.test(ownMessage))) {
      return;
    }
    const message = (context.prefix ? `${context.prefix}: ${ownMessage}` : ownMessage).slice(0, MAX_MESSAGE);
    const key = `${kind}|${context.callSid ?? ''}|${message}`;

    const existing = this.pending.get(key);
    if (existing) {
      existing.count++;
      return;
    }
    if (this.seen.has(key)) {
      // Already sent once this page load: a repeat is not worth another request.
      return;
    }
    if (this.seen.size >= MAX_DISTINCT_PER_SESSION || this.pending.size >= MAX_PENDING) {
      return;
    }
    this.seen.add(key);

    const err = error as { name?: unknown; stack?: unknown; code?: unknown; status?: unknown };
    const name = typeof err?.name === 'string' ? err.name.slice(0, MAX_NAME) : undefined;
    const stack = typeof err?.stack === 'string' ? err.stack.slice(0, MAX_STACK) : undefined;
    const code = context.code ?? this.codeOf(error);
    const route = this.currentRoute();
    const callSid = context.callSid && CALL_SID.test(context.callSid) ? context.callSid : undefined;

    this.pending.set(key, {
      kind,
      message: message || 'Unknown error',
      ...(name && { name }),
      ...(stack && { stack }),
      ...(code !== undefined && { code }),
      ...(route && { route }),
      ...(callSid && { callSid }),
      count: 1,
      occurredAt: new Date().toISOString(),
    });
    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.flushTimer) {
      return;
    }
    // Outside Angular, so the timer does not trigger change detection or hold the app "unstable".
    this.zone.runOutsideAngular(() => {
      this.flushTimer = setTimeout(() => this.flush(), FLUSH_DELAY_MS);
    });
  }

  private messageOf(error: unknown): string {
    if (typeof error === 'string') {
      return error;
    }
    if (error instanceof HttpErrorResponse) {
      return error.message;
    }
    const message = (error as { message?: unknown })?.message;
    return typeof message === 'string' ? message : String(error);
  }

  private codeOf(error: unknown): number | undefined {
    if (error instanceof HttpErrorResponse) {
      return error.status;
    }
    const code = (error as { code?: unknown })?.code;
    return typeof code === 'number' && Number.isInteger(code) ? code : undefined;
  }

  private currentRoute(): string | undefined {
    const route = (this.router.url || '').split('?')[0].split('#')[0];
    return SAFE_ROUTE.test(route) ? route : undefined;
  }
}
